'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { contactSchema } from '../../../types/contact/contactSchema';
import type { ContactFormType } from '../../../types/contact/ContactFormType';
import type { ContactFormResponse } from '../../../types/contact/ContactFormResponse';
import { Logger } from '@shared/logger';
import { FetchApiClient } from '@shared/httpService';
import { PROJECT_NAME } from '@shared/constants';

interface ContactFormProps {
  formType: ContactFormType;
  onSuccess: () => void;
}

type FormData = {
  name: string;
  email: string;
  phone?: string;
  message: string;
  _website?: string; // Honeypot field
};

/**
 * @group Contact
 * 
 * @summary
 * <blockquote>Contact form component with validation and spam protection. 
 * Includes hidden honeypot field for spam detection and preserves form 
 * data on error for retry.</blockquote>
 * 
 * @param formType - Type of contact form ('demo' or 'clinician')
 * @param onSuccess - Callback function called after successful submission
 * 
 * @example
 * ```tsx
 * <ContactForm 
 *   formType="demo" 
 *   onSuccess={() => console.log('Form submitted!')} 
 * />
 * ```
 */
export function ContactForm({ formType, onSuccess }: ContactFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(
      contactSchema
        .extend({
          _website: contactSchema.shape.phone, // Honeypot - same validation as phone (optional string)
        })
        .omit({ formType: true })
    ),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
      _website: '', // Honeypot - should remain empty
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    // Log submission attempt (no PII)
    Logger.info('Contact form submission attempt', { formType });

    // Create API client configured for local service
    const apiClient = new FetchApiClient();

    // Make POST request with Result pattern
    const postResult = await apiClient.post<ContactFormResponse>('/api/contact', {
      name: data.name,
      email: data.email,
      phone: data.phone,
      message: data.message,
      formType,
      _website: data._website, // Honeypot field
    });

    // Handle result using .match pattern
    postResult.match({
      ok: (response) => {
        setIsSubmitting(false);
        
        // Check if API returned success
        if (response.data.success) {
          Logger.info('Contact form submission success', { formType });
          setSubmitSuccess(true);
          reset();
          onSuccess();
        } else {
          // API returned success:false (validation error, etc.)
          // ⚠️ IMPORTANT: Keep exact error message for consistency
          setSubmitError(response.data.message || 'Failed to send message. Please try again.');
          Logger.info('Contact form submission failed', { formType });
        }
      },
      err: (error) => {
        setIsSubmitting(false);
        
        // Handle network errors or other failures
        if (error.errorCode === 'NETWORK_ERROR' || error.errorCode === 'TIMEOUT') {
          // ⚠️ IMPORTANT: Keep exact error message for consistency
          setSubmitError('Network error. Please try again.');
        } else if (error.errorMessage?.includes('429') || error.errorMessage?.includes('rate limit')) {
          // ⚠️ IMPORTANT: Keep exact error message for consistency
          setSubmitError('Too many requests. Please try again later.');
        } else {
          // ⚠️ IMPORTANT: Keep exact error message for consistency
          setSubmitError('Failed to send message. Please try again.');
        }
        
        Logger.info('Contact form network error', { 
          formType, 
          errorCode: error.errorCode 
        });
      },
    });
  };

  const buttonLabel = formType === 'demo' ? 'Request Demo' : 'Submit Application';

  if (submitSuccess) {
    return (
      <div className="text-center py-8">
        <div className="text-primary text-5xl mb-4">✓</div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Message Sent!</h3>
        <p className="text-muted-foreground">
          Thank you for your interest. We'll be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Honeypot field - hidden from users, bots will fill it */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <label htmlFor="_website">Website (leave blank)</label>
        <input
          type="text"
          id="_website"
          autoComplete="off"
          tabIndex={-1}
          {...register('_website')}
        />
      </div>

      {/* Name field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.name ? 'border-red-500' : 'border-input'
          }`}
          placeholder="Your full name"
          {...register('name')}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
        )}
      </div>

      {/* Email field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.email ? 'border-red-500' : 'border-input'
          }`}
          placeholder="your.email@company.co.za"
          {...register('email')}
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>

      {/* Phone field (optional) */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="tel"
          id="phone"
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="082 123 4567"
          {...register('phone')}
        />
      </div>

      {/* Message field */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          Message <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          rows={4}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
            errors.message ? 'border-red-500' : 'border-input'
          }`}
          placeholder={
            formType === 'demo'
              ? 'Tell us about your company and what you\'re looking for...'
              : `Tell us about your experience and interest in joining ${PROJECT_NAME}...`
          }
          {...register('message')}
        />
        {errors.message && (
          <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
        )}
      </div>

      {/* Error message */}
      {submitError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full py-3 px-4 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <span role="status" className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Sending...
          </span>
        ) : (
          buttonLabel
        )}
      </button>
    </form>
  );
}
